import { useEffect, useCallback, useState, useMemo } from "react";
import {
  StoryboardRequiredFields,
  useIframePoolStore,
} from "@/components/Canvas/IframePool/iframePoolStore";
import { useCanvasId } from "@/utils/utils";
import { STORYBOARD_TYPES } from "@/utils/constants";
import { Storyboard } from "@/utils/types";
import { useCodebaseStore } from "@/store/zustand/codebaseStore";
import { useDevServerLaunched } from "@/utils/devServerUtils";

const storyboardHasFilePath = (
  storyboard: Storyboard,
): storyboard is Storyboard & { fullFilePath: string } => {
  return Boolean(storyboard.fullFilePath && storyboard.fullFilePath.length > 0);
};

/**
 * Hook that handles automatic registration/unregistration of storyboards with the iframe pool
 */
export const useStoryboardRegistration = (storyboard: Storyboard) => {
  const canvasId = useCanvasId();

  const maxHotReloadTs = useIframePoolStore((state) => state.maxHotReloadTs);

  const applicationStoryboardsRegistered = useIframePoolStore((state) =>
    Object.keys(state.applicationStoryboards),
  );
  const registeredStoryboardsRegistered = useIframePoolStore((state) =>
    Object.keys(state.registeredStoryboards),
  );

  const registered = useMemo(() => {
    return Boolean(
      applicationStoryboardsRegistered.includes(storyboard.id) ||
        registeredStoryboardsRegistered.includes(storyboard.id),
    );
  }, [
    applicationStoryboardsRegistered,
    registeredStoryboardsRegistered,
    storyboard.id,
  ]);

  const devServerLaunched = useDevServerLaunched();

  // Register storyboard when mounted, unregister when unmounted
  useEffect(() => {
    if (!devServerLaunched) {
      return;
    }

    if (registered) {
      return;
    }

    const storyboardNeedsHtml =
      storyboard?.created || storyboard?.type === STORYBOARD_TYPES.APPLICATION;
    if (!storyboard?.id || !storyboardNeedsHtml) {
      console.debug(
        "[TEMPO DEBUG] storyboard not registered - not yet created",
        {
          storyboardId: storyboard?.id,
          storyboardNeedsHtml,
          storyboard,
        },
      );
      return;
    }

    // If the storyboard is not guaranteed created yet don't load
    if (
      storyboard?.createdHotReloadTs &&
      maxHotReloadTs &&
      storyboard?.createdHotReloadTs > maxHotReloadTs &&
      storyboard.type !== STORYBOARD_TYPES.APPLICATION
    ) {
      console.debug(
        "[TEMPO DEBUG] storyboard not registered - hot reload hasn't happened yet",
        {
          storyboardId: storyboard?.id,
          storyboardNeedsHtml,
          storyboard,
        },
      );
      return;
    }

    if (
      !storyboard.fullFilePath &&
      storyboard.type !== STORYBOARD_TYPES.APPLICATION
    ) {
      console.debug(
        "[TEMPO DEBUG] storyboard not registered - no file path for storyboard",
        {
          storyboardId: storyboard?.id,
          storyboardNeedsHtml,
          storyboard,
        },
      );
      return;
    }

    console.debug("[TEMPO DEBUG] storyboard registered", {
      storyboardId: storyboard?.id,
    });

    // Get store functions inside effect to avoid dependency issues
    const { registerStoryboard } = useIframePoolStore.getState();

    // Ensure storyboard has canvasId for fileKey extraction
    const requiredSbFields: StoryboardRequiredFields = {
      id: storyboard.id,
      type: storyboard.type,
      fullFilePath: storyboard.fullFilePath || "",
      storyComponentName: storyboard.storyComponentName,
      name: storyboard.name,
    };

    // Register with the iframe pool
    registerStoryboard(
      storyboard.id,
      requiredSbFields,
      canvasId,
      storyboard.width,
      storyboard.height,
      storyboard.x,
      storyboard.y,
    );
  }, [
    storyboard?.id,
    storyboard?.created,
    storyboard?.createdHotReloadTs,
    canvasId,
    maxHotReloadTs,
    registered,
    devServerLaunched,
  ]);

  // Update storyboard size when it changes
  useEffect(() => {
    if (storyboard.width && storyboard.height) {
      useIframePoolStore
        .getState()
        .updateStoryboardSize(
          storyboard.id,
          storyboard.width,
          storyboard.height,
          storyboard.x,
          storyboard.y,
        );
    }
  }, [
    storyboard.id,
    storyboard.width,
    storyboard.height,
    storyboard.x,
    storyboard.y,
  ]);

  useEffect(() => {
    if (!registered || !storyboardHasFilePath(storyboard)) {
      return;
    }
    useIframePoolStore
      .getState()
      .updateRegisteredStoryboardData(storyboard.id, storyboard);
  }, [registered, storyboard]);

  /**
   * Unregister if project launch status changes to not succeeded
   */
  useEffect(() => {
    if (!devServerLaunched && registered) {
      useIframePoolStore.getState().unregisterStoryboard(storyboard.id);
    }
  }, [devServerLaunched]);

  useEffect(() => {
    // Cleanup on unmount
    return () => {
      useIframePoolStore.getState().unregisterStoryboard(storyboard.id);
    };
  }, []);
};
